/**
 * Dosyayı özgün adıyla indirme.
 *
 * SORUN: `<a href={firebaseUrl} download="Ozgecmis.docx">` çalışmıyor.
 * HTML'in `download` özniteliği yalnızca **aynı origin** üzerindeki bağlantılarda
 * geçerlidir; Firebase Storage adresleri (firebasestorage.googleapis.com)
 * uygulamadan farklı bir origin olduğu için tarayıcı bu özniteliği yok sayar ve
 * dosyayı Storage'daki nesne adıyla ("<token>-1783681419611.docx") kaydeder.
 *
 * ÇÖZÜM: Dosyayı fetch ile blob olarak indirip aynı origin'de bir blob URL
 * üretmek. Blob URL'de `download` özniteliği geçerli olduğu için dosya
 * kullanıcının gördüğü adla kaydedilir.
 *
 * ÖNEMLİ: Bu yöntem bucket'ta CORS tanımı olmasını gerektirir; aksi hâlde fetch
 * engellenir ve aşağıdaki son çare (adresi açmak) devreye girer. Tanım
 * projedeki cors.json dosyasındadır:
 *   node scripts/fix-storage-download-names.js --apply
 * Aynı betik Storage nesnelerinin Content-Disposition başlığına özgün dosya
 * adını yazar; böylece son çare yolunda bile doğru ad kullanılır.
 */

/** Dosya adında kullanılamayacak karakterleri temizler. */
const sanitizeFileName = (name) =>
  String(name || 'dosya')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .trim() || 'dosya';

/** URL'den uzantı çıkarır (sorgu parametreleri yok sayılır). */
const extensionFromUrl = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\.([A-Za-z0-9]{1,8})$/);
    return match ? match[1] : '';
  } catch (e) {
    return '';
  }
};

/**
 * Verilen adresteki dosyayı `fileName` adıyla indirir.
 *
 * @param {string} url Dosyanın indirme adresi
 * @param {string} fileName Kullanıcıya gösterilen özgün dosya adı
 * @returns {Promise<boolean>} indirme başlatılabildi mi
 */
export const downloadFileAs = async (url, fileName) => {
  if (!url) return false;

  let safeName = sanitizeFileName(fileName);
  // Ad uzantısız kaldıysa adresteki uzantıyı ekle
  if (!/\.[A-Za-z0-9]{1,8}$/.test(safeName)) {
    const ext = extensionFromUrl(url);
    if (ext) safeName = `${safeName}.${ext}`;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`İndirme başarısız: ${response.status}`);
    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Tarayıcının indirmeyi başlatmasına fırsat ver, sonra belleği bırak
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    return true;
  } catch (error) {
    console.error('Dosya indirilemedi, adres yeni sekmede açılıyor:', error);
    // CORS engeli gibi durumlarda dosyaya erişimi tamamen kaybetmemek için
    // son çare olarak adresi aç. Nesnenin Content-Disposition başlığı özgün adı
    // taşıdığı için bu yolda da doğru ad kullanılır.
    window.open(url, '_blank', 'noopener,noreferrer');
    return false;
  }
};

export default downloadFileAs;
